// ─── Cuan · auth.js ───────────────────────────────────────────────────────────
// AuthProvider  — wraps the whole app; shows LoginScreen when not authenticated.
// useAuth       — hook to access { user, signOut } anywhere in the app.
// SignOutButton — drop-in button you can place in the app header/nav.

import React, { createContext, useContext, useState, useEffect } from 'react';
import { getStoredSession, authSignIn, authSignOut, authGetUser } from './supabase';

// ── Context ────────────────────────────────────────────────────────────────────

const AuthContext = createContext(null);

/** Access the current user and signOut() from anywhere inside AuthProvider. */
export function useAuth() {
  return useContext(AuthContext);
}

// ── Login Screen ───────────────────────────────────────────────────────────────

function LoginScreen({ onLogin }) {
  const [email,       setEmail]       = useState('');
  const [password,    setPassword]    = useState('');
  const [showPass,    setShowPass]    = useState(false);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await authSignIn(email, password);
      await onLogin();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '11px 14px',
    border: '1.5px solid #e2e8f0',
    borderRadius: 8,
    fontSize: 15,
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: "'DM Sans', system-ui, sans-serif",
    color: '#1A2740',
    background: '#fff',
    transition: 'border-color 0.15s',
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg, #1A2740 0%, #0f172a 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: "'DM Sans', system-ui, sans-serif",
      padding: 16,
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 20,
        padding: '48px 40px 40px',
        width: '100%',
        maxWidth: 400,
        boxShadow: '0 24px 64px rgba(0,0,0,0.35)',
      }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 52, height: 52, borderRadius: 14, background: '#1A2740',
            marginBottom: 16, fontSize: 24,
          }}>⚓</div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1A2740', letterSpacing: '-0.3px' }}>
            Sign in to Cuan
          </h1>
          <p style={{ margin: '6px 0 0', fontSize: 14, color: '#64748b' }}>
            Your family's safe harbor
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} noValidate>

          {/* Email */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder="you@example.com"
              style={inputStyle}
              onFocus={e => e.target.style.borderColor = '#2563EB'}
              onBlur={e  => e.target.style.borderColor = '#e2e8f0'}
            />
          </div>

          {/* Password with show/hide toggle */}
          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
              Password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPass ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                placeholder="••••••••"
                style={{ ...inputStyle, paddingRight: 44 }}
                onFocus={e => e.target.style.borderColor = '#2563EB'}
                onBlur={e  => e.target.style.borderColor = '#e2e8f0'}
              />
              <button
                type="button"
                onClick={() => setShowPass(p => !p)}
                style={{
                  position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: 16, color: '#94a3b8', padding: 0, lineHeight: 1,
                }}
                title={showPass ? 'Hide password' : 'Show password'}
              >
                {showPass ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div style={{
              background: '#fef2f2', border: '1.5px solid #fecaca', borderRadius: 8,
              padding: '10px 14px', color: '#dc2626', fontSize: 14, marginBottom: 16,
            }}>
              {error}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              background: loading ? '#93c5fd' : '#2563EB',
              color: '#fff', border: 'none', borderRadius: 10, padding: '12px',
              fontSize: 15, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit', letterSpacing: '0.01em',
              transition: 'background 0.15s, transform 0.1s',
            }}
            onMouseEnter={e => { if (!loading) e.target.style.background = '#1d4ed8'; }}
            onMouseLeave={e => { if (!loading) e.target.style.background = '#2563EB'; }}
          >
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>

        {/* Footer */}
        <p style={{ textAlign: 'center', marginTop: 24, marginBottom: 0, fontSize: 12, color: '#94a3b8' }}>
          cuan.money · family finances, in one harbor
        </p>
      </div>
    </div>
  );
}

// ── Loading Screen ─────────────────────────────────────────────────────────────

function LoadingScreen() {
  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg, #1A2740 0%, #0f172a 100%)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'DM Sans', system-ui, sans-serif", gap: 16,
    }}>
      <div style={{ fontSize: 36 }}>⚓</div>
      <p style={{ color: '#93c5fd', fontSize: 15, margin: 0 }}>Loading…</p>
    </div>
  );
}

// ── AuthProvider ───────────────────────────────────────────────────────────────

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    const session = getStoredSession();
    if (session?.access_token) {
      authGetUser()
        .then(u => setUser(u || null))
        .catch(() => setUser(null));
    } else {
      setUser(null);
    }
  }, []);

  const handleLogin = async () => {
    const u = await authGetUser();
    setUser(u);
  };

  const handleLogout = async () => {
    await authSignOut();
    setUser(null);
  };

  if (user === undefined) return <LoadingScreen />;
  if (!user)              return <LoginScreen onLogin={handleLogin} />;

  return (
    <AuthContext.Provider value={{ user, signOut: handleLogout }}>
      {children}
    </AuthContext.Provider>
  );
}

// ── SignOutButton ──────────────────────────────────────────────────────────────

export function SignOutButton({ style = {} }) {
  const { user, signOut } = useAuth();

  return (
    <button
      onClick={signOut}
      title={`Signed in as ${user?.email}`}
      style={{
        background: 'transparent',
        border: '1.5px solid rgba(255,255,255,0.25)',
        borderRadius: 8,
        color: 'rgba(255,255,255,0.75)',
        fontSize: 13,
        fontWeight: 500,
        padding: '6px 14px',
        cursor: 'pointer',
        fontFamily: 'inherit',
        transition: 'all 0.15s',
        ...style,
      }}
      onMouseEnter={e => { e.target.style.background = 'rgba(255,255,255,0.1)'; e.target.style.color = '#fff'; }}
      onMouseLeave={e => { e.target.style.background = 'transparent'; e.target.style.color = 'rgba(255,255,255,0.75)'; }}
    >
      Sign out
    </button>
  );
}
